import { apiCall, getOptionalSessionId } from "./Network";
import { setItem } from "./Utils";
import { convertCamelToSnakeCase } from "./Utils";

export class APIs {
  static async login(email, password) {
    return await apiCall({
      endpoint: "login",
      authRequired: false,
      body: {
        email,
        password,
      },
    });
  }

  static async register(
    name,
    email,
    password,
    provider = "email",
    businessUrl,
    verificationCode = null,
  ) {
    const body = convertCamelToSnakeCase({
      name,
      email,
      password,
      provider,
      businessUrl,
    });
    if (verificationCode) {
      body.verification_code = verificationCode;
    }
    return await apiCall({
      endpoint: "register",
      authRequired: false,
      body,
    });
  }

  static async getPrelimBusinessDetails(businessUrl) {
    return await apiCall({
      endpoint: "getPrelimBusinessDetails",
      authRequired: false,
      body: convertCamelToSnakeCase({
        businessUrl,
      }),
    });
  }

  static async saveBusinessDatabase(
    dbUsername,
    dbPassword,
    dbUrl,
    dbType,
    dbName,
    dbPort,
  ) {
    return apiCall({
      endpoint: "saveBusinessDatabase",
      body: convertCamelToSnakeCase({
        dbUsername,
        dbPassword,
        dbUrl,
        dbType,
        dbName,
        dbPort,
      }),
    });
  }

  static async getSegments(
    businessName,
    businessUrl,
    businessDescription,
    stakeholders,
    associatedUrls,
  ) {
    return apiCall({
      endpoint: "getSegments",
      authRequired: false,
      body: convertCamelToSnakeCase({
        businessName,
        businessUrl,
        businessDescription,
        stakeholders,
        associatedUrls,
      }),
    });
  }

  static async getBusinessExamples(
    businessName,
    businessUrl,
    businessDescription,
    stakeholders,
    associatedUrls,
  ) {
    return apiCall({
      endpoint: "getBusinessExamples",
      authRequired: false,
      body: convertCamelToSnakeCase({
        businessName,
        businessUrl,
        businessDescription,
        stakeholders,
        associatedUrls,
      }),
    });
  }

  static async getSegmentsWaitingMessages(
    businessName,
    businessUrl,
    businessDescription,
    stakeholders,
  ) {
    return apiCall({
      endpoint: "getSegmentsWaitingMessages",
      authRequired: false,
      body: convertCamelToSnakeCase({
        businessName,
        businessUrl,
        businessDescription,
        stakeholders,
      }),
    });
  }

  static async joinPilot(email) {
    return apiCall({
      endpoint: "joinPilot",
      authRequired: false,
      body: {
        email,
      },
    });
  }

  static async getBusinessDetails(businessUrl) {
    return apiCall({
      endpoint: "getBusinessDetails",
      authRequired: false,
      body: convertCamelToSnakeCase({
        businessUrl,
      }),
    });
  }

  static async getSegmentActionDetails(actionId) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "getSegmentActionDetails",
      body: convertCamelToSnakeCase({
        actionId,
        ...(sessionid && { sessionid }),
      }),
      authRequired: false,
    });
  }

  static async getSegmentActionDetailsWaitingMessages(actionId) {
    const sessionid = getOptionalSessionId();

    return apiCall({
      endpoint: "getSegmentActionDetailsWaitingMessages",
      authRequired: false,
      body: convertCamelToSnakeCase({
        actionId,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async getDashboardDetails() {
    return apiCall({
      endpoint: "getDashBoardDetails",
      authRequired: true,
    });
  }

  static async getSettingsDetails() {
    return apiCall({
      endpoint: "getSettingsDetails",
    });
  }

  static async generateSampleDatabase() {
    return apiCall({
      endpoint: "generateSampleDatabase",
    });
  }

  static async updateSettings(active) {
    return apiCall({
      endpoint: "updateSettings",
      body: convertCamelToSnakeCase({
        active,
      }),
    });
  }

  static async resetPass(email, code = null, password = null) {
    const body = { email };
    if (code) {
      body.code = code;
    }
    if (password) {
      body.password = password;
    }
    return apiCall({
      endpoint: "resetPass",
      authRequired: false,
      body,
    });
  }

  static async addSegment(stakeholderId, segmentDescription) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "addSegment",
      authRequired: false,
      body: convertCamelToSnakeCase({
        stakeholderId,
        segmentDescription,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async enableSegment(segmentId, enable) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "enableSegment",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        enable,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async approveSegment(segmentId, approved) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "approveSegment",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        approved,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async approveSegmentAction(actionId, approved) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "approveSegmentAction",
      authRequired: false,
      body: convertCamelToSnakeCase({
        actionId,
        approved,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async getSegmentAnalytics(segmentId) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "getSegmentAnalytics",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async deleteSegment(segmentId) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "deleteSegment",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async addSegmentAction(segmentId, description) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "addSegmentAction",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        description,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async deleteSegmentAction(actionId) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "deleteSegmentAction",
      authRequired: false,
      body: convertCamelToSnakeCase({
        actionId,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async editSegmentAction(
    actionId,
    emailSubject = null,
    emailContent = null,
    delay = null,
    delayUnit = null,
  ) {
    const sessionid = getOptionalSessionId();
    const body = convertCamelToSnakeCase({
      actionId,
      ...(sessionid && { sessionid }),
    });

    if (emailSubject !== null) {
      body.email_subject = emailSubject;
    }
    if (emailContent !== null) {
      body.email_content = emailContent;
    }
    if (delay !== null) {
      body.delay = delay;
    }
    if (delayUnit !== null) {
      body.delay_unit = delayUnit;
    }

    return apiCall({
      endpoint: "editSegmentAction",
      authRequired: false,
      body,
    });
  }

  static async sendTestEmail(toEmail, subject, html) {
    return apiCall({
      endpoint: "sendTestEmail",
      authRequired: false,
      body: convertCamelToSnakeCase({
        toEmail,
        subject,
        html,
      }),
    });
  }

  static async addStakeholder(
    stakeholderName,
    stakeholderDescription,
    businessUrl,
  ) {
    let sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "addStakeholder",
      authRequired: false,
      body: convertCamelToSnakeCase({
        stakeholderName,
        stakeholderDescription,
        businessUrl,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async getSegmentDetails(segmentId) {
    const sessionid = getOptionalSessionId();
    return apiCall({
      endpoint: "getSegmentDetails",
      authRequired: false,
      body: convertCamelToSnakeCase({
        segmentId,
        ...(sessionid && { sessionid }),
      }),
    });
  }

  static async editSegment(
    segmentId,
    query = null,
    humanExplanationQuery = null,
  ) {
    const sessionid = getOptionalSessionId();
    const body = convertCamelToSnakeCase({
      segmentId,
      ...(sessionid && { sessionid }),
    });
    if (query !== null) {
      body.query = query;
    }
    if (humanExplanationQuery !== null) {
      body.human_explanation_query = humanExplanationQuery;
    }
    return apiCall({
      endpoint: "editSegment",
      authRequired: false,
      body,
    });
  }
}
